import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { isValid, parseISO } from "date-fns";
import { useCallback, useState } from "react";

import { InputContainer } from "../InputContainer";

type DatePickerControlProps = {
  value?: Date | string;
  label?: string;
  alignLabel?: boolean;
  onChange: (date?: Date) => void;
  type?: "date" | "dateTime";
  disabled?: boolean;
  maxDate?: Date | string;
  minDate?: Date | string;
  inChart?: boolean;
  validation?: boolean;
};

function DatePickerControl({
  value,
  label,
  alignLabel,
  onChange,
  disabled,
  type = "date",
  maxDate,
  minDate,
  inChart,
  validation = true,
}: DatePickerControlProps) {
  // Parse initial value if it's a string
  const initialValue = value ? (typeof value === "string" ? parseISO(value) : value) : undefined;

  const [dateValue, setDateValue] = useState<Date | undefined>(initialValue);

  // Parse min/max dates if they're strings
  const parsedMinDate = minDate
    ? typeof minDate === "string"
      ? parseISO(minDate)
      : minDate
    : undefined;

  const parsedMaxDate = maxDate
    ? typeof maxDate === "string"
      ? parseISO(maxDate)
      : maxDate
    : undefined;

  const handleDateChange = useCallback(
    (newDate: Date | null) => {
      const validDate = newDate && isValid(newDate) ? newDate : undefined;
      setDateValue(validDate);
      onChange(validDate);
    },
    [onChange]
  );

  const commonProps = {
    disabled,
    value: dateValue || null,
    onChange: handleDateChange,
    minDate: parsedMinDate || null,
    maxDate: parsedMaxDate || null,
    slotProps: {
      actionBar: {
        actions: ["clear"],
      },
      textField: {
        variant: "standard",
        fullWidth: true,
      },
    },
  };

  return (
    <InputContainer alignLabel={alignLabel} label={label} className={inChart ? "inChart" : ""}>
      {type === "date" ? (
        <DatePicker {...commonProps} views={["month", "day", "year"]} />
      ) : (
        <DateTimePicker
          {...commonProps}
          ampm={false}
          views={["month", "day", "year", "hours", "minutes"]}
        />
      )}
    </InputContainer>
  );
}

export default DatePickerControl;
